import { useTranslation } from 'react-i18next';
import type { DeviceDto } from '@gc/shared';

import { timeOfDay } from './bookingLogic';

interface StartOverBookingConfirmProps {
  device: DeviceDto;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Soft confirm before starting a walk-in on a machine with an upcoming
 * booking (WAVE2-DESIGN §1). Soft on purpose: the venue's judgement beats the
 * system's — a 20-minute round before an 18:00 booking is fine — so this only
 * makes sure nobody starts a three-hour session over it unknowingly.
 */
export function StartOverBookingConfirm({
  device,
  onConfirm,
  onClose,
}: StartOverBookingConfirmProps) {
  const { t } = useTranslation();
  const booking = device.nextBooking;
  if (!booking) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('bookings.confirmStart.title')}
        className="fixed inset-0 z-50 grid place-items-center p-4"
      >
        <div className="w-full max-w-sm rounded-card border border-subtle bg-ground p-4 shadow-2xl">
          <h2 className="text-base font-semibold text-content">
            {t('bookings.confirmStart.title')}
          </h2>
          <p className="mt-1 text-sm text-content-secondary">
            {t('bookings.confirmStart.message', {
              device: device.name,
              name: booking.partyName,
              time: timeOfDay(booking.startsAt),
            })}
          </p>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-1.5 text-sm text-content-secondary hover:bg-hover hover:text-content"
            >
              {t('bookings.confirmStart.back')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover"
            >
              {t('bookings.confirmStart.confirm')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
